import React, { useCallback, useEffect, useState } from "react";
import ChevronRightIcon from "@atlaskit/icon/glyph/chevron-right";
import EditorAddIcon from "@atlaskit/icon/glyph/editor/add";
import EditorRemoveIcon from "@atlaskit/icon/glyph/editor/remove";
import Tooltip from "@atlaskit/tooltip";
import {
  ClickableIcon,
  IconContainer,
  ItemContainer,
  ItemWrapper,
  LabelWrapper,
  ListWrapper,
  RotateableIconContainer,
  UserActionsWrapper,
} from "./styled";
import { DotIcon } from "./Icons";
import { StyledLabel } from "../picker/StyledHelpers";
import { PickerOptionTypes } from "../picker/option-types";
import { useDrag, useDrop } from "react-dnd";
import DropPlaceholder from "./DropPlaceholder";
import { useTheme } from "styled-components";
import AddPeopleController from "../enrichment/AddPeopleController";
import { useStructureStore } from "../store/structure-store";
import { useActionsStore } from "../store/actions-store";
import { getUserByAccountId } from "../../../../../../../api/people-api";

const buildUserObject = (user) => ({
  renderLabel: () => <StyledLabel type={PickerOptionTypes.USER} data={user} />,
  value: user.accountId,
  type: PickerOptionTypes.USER,
  name: user.displayName,
  childrenCount: user.childrenCount,
});

function ListItem({ node, itemLevel = 0 }) {
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState();
  const [isExpanded, setIsExpanded] = useState(true);

  const theme = useTheme();

  const { selectedUser, setSelectedUser, removeUser, reorder } =
    useStructureStore((state) => ({
      selectedUser: state.selectedUser,
      setSelectedUser: state.setSelectedUser,
      removeUser: state.removeUser,
      reorder: state.reorder,
    }));

  const { isAddingPeople, setIsAddingPeople } = useActionsStore((state) => ({
    isAddingPeople: state.isAddingPeople,
    setIsAddingPeople: state.setIsAddingPeople,
  }));

  const { id: userId, children } = node;
  const { renderLabel, name } = userInfo || {};

  const hasChildren = !!children?.length;

  const [{ isOver }, drop] = useDrop(() => ({
    accept: ["user"],
    drop: () => ({ type: "user", accountId: userId }),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const [{ isDragging }, drag] = useDrag({
    type: "user",
    item: () => {
      return { type: "user", accountId: userId };
    },
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (item && dropResult) {
        reorder(item, dropResult);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    if (userId) {
      setIsLoading(true);
      (async () => {
        try {
          const userResponse = await getUserByAccountId(userId);
          const user = JSON.parse(userResponse.body);

          if (user) {
            setUserInfo(buildUserObject(user));
          }
        } catch (error) {
          setUserInfo(undefined);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [userId]);

  const isAddingPeopleToUser = useCallback(
    (userIdInList) => {
      if (!isAddingPeople || !selectedUser?.id) return false;
      return userIdInList === selectedUser?.id;
    },
    [isAddingPeople, selectedUser]
  );

  if (isLoading || !userInfo) {
    return (
      <ItemWrapper key={userId} itemLevel={itemLevel}>
        <ItemContainer>Loading...</ItemContainer>
      </ItemWrapper>
    );
  }

  return (
    <>
      <ItemWrapper key={userId} itemLevel={itemLevel} ref={drag}>
        <ItemContainer
          theme={theme}
          ref={drop}
          isHoveringOver={isOver}
          isSelected={userId === selectedUser?.id}
          onClick={() => {
            if (selectedUser?.id === userId) {
              setSelectedUser(undefined);
            } else {
              setSelectedUser({ id: userId, name });
            }
          }}
        >
          <LabelWrapper>
            {hasChildren ? (
              <RotateableIconContainer
                shouldRotate={isExpanded || isDragging}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
              >
                <ChevronRightIcon primaryColor="#97a0af" />
              </RotateableIconContainer>
            ) : (
              <IconContainer>
                <DotIcon color="#97a0af" />
              </IconContainer>
            )}

            {renderLabel()}
          </LabelWrapper>
          <UserActionsWrapper>
            <Tooltip content="Add below">
              <ClickableIcon
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsExpanded(true);
                  setSelectedUser({ id: userId, name });
                  setIsAddingPeople(true);
                }}
              >
                <EditorAddIcon />
              </ClickableIcon>
            </Tooltip>

            <Tooltip content="Remove">
              <ClickableIcon
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeUser(userId);
                }}
              >
                <EditorRemoveIcon />
              </ClickableIcon>
            </Tooltip>
          </UserActionsWrapper>
        </ItemContainer>
      </ItemWrapper>
      <DropPlaceholder
        userId={userId}
        name={name}
        itemLevel={itemLevel}
        isDraggingUser={isDragging}
      />

      {isAddingPeopleToUser(userId) && <AddPeopleController />}

      {hasChildren && (isExpanded || isDragging) && (
        <ListWrapper>
          {node.children.map((node) => (
            <ListItem key={node.id} node={node} itemLevel={itemLevel + 1} />
          ))}
        </ListWrapper>
      )}
    </>
  );
}

export default ListItem;
